import {
  EntityCardBlueprint,
  EntityContentLayoutBlueprint,
  EntityHeaderLayoutBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  createFrontendModule,
  createFrontendPlugin,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { PROJECT_ENTITY_FILTER } from '../shared';
import { BRANCH_PAGE_PATH, branchRouteRef } from './branchRouteRef';
import { projectEnvironmentsFrTranslation } from './translation';

// What makes an entity a project here: the cards, the overview layout and
// the header layout below all key on it — the type annotation
// `waadoo.ovh/app-type`, `PROJECT_ENTITY_FILTER`
// (components/shared/projectIdentity.ts). The app-config files hide some
// upstream cards with its exact mirror image (the annotation
// `$exists: false`) — keep them in sync.
const projectFilter = PROJECT_ENTITY_FILTER;

// « Projet »: name, description, links and tags of the project, in place of
// the catalog's About and Links cards, which the app-config files hide on an
// Odoo project with the mirror image of this filter. An `info` card, so the
// overview layout puts it on top; named first in `app.extensions`, so it
// comes before any other info card.
const projectEntityCard = EntityCardBlueprint.make({
  name: 'project',
  params: {
    type: 'info',
    filter: projectFilter,
    loader: async () => {
      const { ProjectCard } = await import('./ProjectCard');
      return <ProjectCard />;
    },
  },
});

// A card is not attached to a given page: it declares a condition, and shows up
// on every entity that satisfies it.
const environmentsEntityCard = EntityCardBlueprint.make({
  name: 'project-environments',
  params: {
    type: 'content',
    filter: projectFilter,
    loader: async () => {
      const { EnvironmentsCard } = await import('./EnvironmentsCard');
      return <EnvironmentsCard />;
    },
  },
});

// « Promotion »: the chain of the project's permanent branches and the
// promotion of one to the next (PromotionCard). Above the environments
// table: named right before it in `app.extensions` (app-config). Renders
// nothing where the backend route does not exist.
const promotionEntityCard = EntityCardBlueprint.make({
  name: 'project-promotion',
  params: {
    type: 'content',
    filter: projectFilter,
    loader: async () => {
      const { PromotionCard } = await import('./PromotionCard');
      return <PromotionCard />;
    },
  },
});

// The overview tab of an Odoo project: info cards on top, the rest below, all
// at full width (see ProjectOverviewLayout). The catalog's overview
// content tries the layouts attached to it in order and falls back to its
// default one, so every other entity keeps the default layout.
const projectOverviewLayout = EntityContentLayoutBlueprint.make({
  name: 'project',
  params: {
    filter: projectFilter,
    loader: async () => {
      const { ProjectOverviewLayout } = await import('./ProjectOverviewLayout');
      return ProjectOverviewLayout;
    },
  },
});

// The header of an Odoo project's page: the house button and the project's
// name, as the branch page's header (PageHeader), instead of the
// catalog's « Catalogue » bar, which styles.css hides while this header is on
// the page (see ProjectHeader). The entity page uses the first header
// layout whose filter accepts the entity, filtered ones first, and its own
// header when none does — every other entity keeps the catalog's header and
// its « Catalogue » bar.
export const projectHeaderLayout = EntityHeaderLayoutBlueprint.make({
  name: 'project',
  params: {
    filter: projectFilter,
    loader: async () => {
      const { ProjectHeader } = await import('./ProjectHeader');
      return ProjectHeader;
    },
  },
});

// The cards and the layouts must stay on the `catalog` plugin: that is the
// plugin owning the entity page they attach to.
export default createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    projectEntityCard,
    environmentsEntityCard,
    promotionEntityCard,
    projectOverviewLayout,
    projectHeaderLayout,
  ],
});

// The translation must go in a separate module on the `app` plugin:
// TranslationBlueprint is restricted to the app plugin, and an extension it
// creates under any other pluginId is ignored without warning or error — the
// card then renders in its default (English) messages. Do not merge these two
// modules back into one.
// Registered in packages/app/src/App.tsx alongside the card module.
export const projectEnvironmentsTranslationModule = createFrontendModule({
  pluginId: 'app',
  extensions: [projectEnvironmentsFrTranslation],
});

const branchPage = PageBlueprint.make({
  name: 'branch',
  params: {
    // The two path segments are the two route-ref params, in order, and the
    // resolver percent-encodes each of them — which is why every link to this
    // page goes through the ref rather than composing the path by hand.
    //
    // The literal itself lives next to the ref: the sidebar matches the current
    // location against the same constant to tell which project is on screen.
    path: BRANCH_PAGE_PATH,
    // The page paints its own header, which carries the project and the branch
    // — the default plugin header above it would only add the plugin id as a title
    // (PageBlueprint falls back to it, this plugin declaring none) and a second
    // horizontal rule. Same choice the catalog makes for its entity
    // page.
    noHeader: true,
    routeRef: branchRouteRef,
    loader: async () => {
      const { BranchPage } = await import('./BranchPage');
      return <BranchPage />;
    },
  },
});

/**
 * A third feature, and a plugin rather than a module.
 *
 * Not a module on `catalog`, which is where the card lives: a page is not part
 * of the catalog plugin, and mounting it there would quietly answer a product
 * question that is explicitly still open — whether the per-branch console lives
 * inside the catalog entity page or beside it (decision D8 in
 * ODOO_IDP_DEMO_DECISIONS.md defers exactly that arbitration). Its own plugin
 * keeps both futures available: the page can gain a nav entry, or be folded
 * into the entity page as a tab, without either move being pre-decided here.
 *
 * NO NAV ITEM, DELIBERATELY. The page is reached from the environments card of
 * a project, and it needs a project and a branch to mean anything — a sidebar
 * entry has neither, so it could only land on a page that cannot load. This is
 * a decision, not an oversight: do not "fix" it by adding a NavItemBlueprint.
 *
 * Plugin id `waadoo-projects` (`odoo` until point 9.2): the page extension id is
 * therefore `page:waadoo-projects/branch`, and no app-config references it.
 *
 * Registered in packages/app/src/App.tsx alongside the two modules above.
 */
export const branchPagePlugin = createFrontendPlugin({
  pluginId: 'waadoo-projects',
  extensions: [branchPage],
});
